import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

const HotelBookings = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const getTodayDate = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localDate = new Date(today.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  const [pageLoading, setPageLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [rawResponse, setRawResponse] = useState({});

  // Pagination States
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter States
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [customerName, setCustomerName] = useState("");
  const [bookingNo, setBookingNo] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [source, setSource] = useState("CLIENT");

  // Amendment Modal States
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const GetData = async (pNum = 1) => {
    if (!user?.cid) return;
    setPageLoading(true);

    const queryParams = new URLSearchParams({
      customerName: customerName || "",
      bookingNo: bookingNo || "",
      bookingStatus: bookingStatus || "",
      paymentStatus: paymentStatus || "",
      fromDate: fromDate || "",
      toDate: toDate || "",
      source: source || "",
      pageNumber: pNum,
      pageSize: pageSize
    }).toString();

    try {
      const url = `${config.GETHOTELBOOKINGSBYPARENTCOMPANYID}?ParentCompanyId=${user?.cid}&${queryParams}`;
      const res = await apiService.post(url);

      if (res) {
        setLists(res.data || []);
        setPageNumber(res.pageNumber || 1);
        setPageSize(res.pageSize || 20);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setRawResponse(res);
      }
    } catch (error) {
      console.error("Failed to fetch hotel bookings:", error);
      setLists([]);
    } finally {
      setPageLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async (hotelBookingId, bookingNo) => {
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete booking #${bookingNo}?`)) {
      return;
    }

    setPageLoading(true);
    try {
      // Call the delete API with isDeleted = true
      const url = `${config.DELETEHOTELBOOKING}/${hotelBookingId}`;
      const result = await apiService.put(url);

      if (result) {
        toast.success(`Booking deleted successfully`);
        // Refresh the list after deletion
        GetData(pageNumber);
      } else {
        toast.error("Failed to delete booking");
      }
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error("An error occurred while deleting the booking");
    } finally {
      setPageLoading(false);
    }
  };

  // Open Amendment Modal
  const openAmendmentModal = (booking) => {
    setSelectedBooking(booking);
    setShowAmendmentModal(true);
  };

  // Close Amendment Modal
  const closeAmendmentModal = () => {
    setShowAmendmentModal(false);
    setSelectedBooking(null);
  };

  // Handle successful amendment submission
  const handleAmendmentSuccess = () => {
    GetData(pageNumber);
  };

  // Pagination Logic
  const GetPageData = (number) => {
    setPageNumber(number);
    GetData(number);
  };

  const GetFirstPageData = () => {
    if (rawResponse.previousPage !== null) GetData(1);
  };

  const GetLastPageData = () => {
    if (rawResponse.nextPage !== null) GetData(totalPages);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const handleSearch = () => {
    setPageNumber(1);
    GetData(1);
  };

  const handleReset = () => {
    const today = getTodayDate();
    setFromDate(today);
    setToDate(today);
    setCustomerName("");
    setBookingNo("");
    setBookingStatus("");
    setPaymentStatus("");
    setSource("CLIENT");
    setPageNumber(1);
    setTimeout(() => GetData(1), 100);
  };

  // Helper function to get status badge color
  const getBookingStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "danger";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "danger";
      case "REFUNDED":
        return "info";
      default:
        return "secondary";
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    return moment(date).format("DD MMM YYYY");
  };

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) { navigate("/"); return null; }

  return (
    <div id="layout-wrapper">
      <Header User={user} activeSection="hotels" />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-body">

                  {/* Filter Section */}
                  <div className="row mb-4 g-3 bg-light p-3 rounded">
                    <div className="col-md-2">
                      <label className="form-label">Check-In From</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Check-Out To</label>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Source</label>
                      <select className="form-select" value={source} onChange={(e) => setSource(e.target.value)}>
                        <option value="CLIENT">PORTAL</option>
                        <option value="CUSTOMER">CUSTOMER</option>
                        <option value="CORPORATE">CORPORATE</option>
                        <option value="AGENT">AGENT</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Guest Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search Guest Name..."
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Booking No</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Booking Reference..."
                        value={bookingNo}
                        onChange={(e) => setBookingNo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Booking Status</label>
                      <select className="form-select" value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)}>
                        <option value="">All Status</option>
                        <option value="CONFIRMED">CONFIRMED</option>
                        <option value="PENDING">PENDING</option>
                        <option value="CANCELLED">CANCELLED</option>
                        <option value="FAILED">FAILED</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Payment Status</label>
                      <select className="form-select" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                        <option value="">All Payment</option>
                        <option value="SUCCESS">SUCCESS</option>
                        <option value="PENDING">PENDING</option>
                        <option value="FAILED">FAILED</option>
                        <option value="REFUNDED">REFUNDED</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">&nbsp;</label>
                      <div className="d-flex gap-2">
                        <button className="btn btn-primary flex-grow-1" onClick={handleSearch}>
                          <i className="ri-search-line me-1"></i> Search
                        </button>
                        <button className="btn btn-danger flex-grow-1" onClick={handleReset}>
                          <i className="ri-refresh-line me-1"></i> Reset
                        </button>
                      </div>
                    </div>
                  </div>



                  {/* Table Section */}
                  <div className="table-responsive table-card p-2">
                    <table className="table align-middle" id="hotelCustomerTable">
                      <thead className="bg-soft-success bg-gradient">
                        <tr>
                          <th>S.No</th>
                          <th>Supplier</th>
                          <th>Booking No</th>
                          {/* <th>Hotel Details</th> */}
                          <th>Destination</th>
                          <th>Check In/Out</th>
                          <th>Guest Name</th>
                          <th>Rooms/Guests</th>
                          <th>Branch</th>
                          <th>Booking Status</th>
                          <th>Payment Status</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="list form-check-all">
                        {pageLoading ? (
                          <tr>
                            <td colSpan={11} className="text-center">
                              <img src="/assets/images/loader.webp" width="64px" height="64px" alt="loader" loading="lazy" />
                              <p>Loading hotel bookings, Please wait...</p>
                            </td>
                          </tr>
                        ) : lists.length > 0 ? (
                          lists.map((v, index) => (
                            <tr key={v.hotelBookingId}>
                              <td>{(pageNumber - 1) * pageSize + (index + 1)}</td>
                              <td>
                                <strong>{v.supplierName}</strong>

                              </td>
                              <td>
                                <strong>{v.bookingNo}</strong>
                                <br />
                                <small className="text-muted">SupplierBookingId: {v.supplierBookingId || "N/A"}</small>

                              </td>
                              {/* <td>
                                <strong>{v.hotelDetails?.hotelName || "N/A"}</strong>
                                <br />
                                <small className="text-muted">{v.hotelDetails?.hotelCategory || ""}</small>
                              </td> */}
                              <td>
                                {v.city}
                                <br />
                                <small className="text-muted">{v.hotelDetails?.hotelAddress}</small>
                              </td>
                              <td>
                                <div>IN: {formatDate(v.checkIn)}</div>
                                <div>OUT: {formatDate(v.checkOut)}</div>

                              </td>
                              <td>
                                {v.travellerName}
                                <br />
                                <small className="text-muted">{v.travellerEmail}</small>
                              </td>
                              <td className="text-center">
                                <div>{v.noOfRooms || 0} Room(s)</div>
                                <small className="text-muted">{v.noOfGuests || 0} Guest(s)</small>
                              </td>
                              <td>{v.branchName}</td>
                              <td>
                                <span className={`badge bg-${getBookingStatusBadge(v.bookingStatus)}`}>
                                  {v.bookingStatus}
                                </span>


                              </td>
                              <td>
                                <span className={`badge bg-${getPaymentStatusBadge(v.paymentStatus)}`}>
                                  {v.paymentStatus}
                                </span>
                              </td>
                              <td>
                                <div>INR{v.totalPrice?.toLocaleString() || 0}</div>
                                <small className="text-muted">
                                  {v.paymentGateway === "WALLET" ? "Wallet" : v.paymentGateway}
                                </small>
                              </td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li className="list-inline-item">
                                    <Link
                                      to={`/hotel/booking/${v.hotelBookingId}`}
                                      className="btn btn-sm btn-primary"
                                    >
                                      <i className="ri-eye-line me-1"></i>
                                    </Link>
                                  </li>
                                  {/* Delete Button */}
                                  {v.bookingStatus !== "CONFIRMED" && v.paymentStatus !== "SUCCESS" && (
                                    <li className="list-inline-item">
                                      <button
                                        onClick={() => handleDelete(v.hotelBookingId, v.bookingNo)}
                                        className="btn btn-sm btn-danger"
                                        title="Delete Booking"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={11} className="text-center">
                              <div className="py-5">
                                <i className="ri-hotel-line fs-1 text-muted"></i>
                                <p className="mt-2 mb-0">No hotel bookings found.</p>

                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Footer */}
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing <span className="fw-semibold">{lists.length}</span> of{" "}
                        <span className="fw-semibold">{totalRecords}</span> Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className={`page-item ${rawResponse.previousPage === null ? "disabled" : ""}`} onClick={GetFirstPageData}>
                        <Link className="page-link" to="#">First</Link>
                      </li>
                      {pageNumbers.map((number) => (
                        <li key={number} className={`page-item ${pageNumber === number ? "active" : ""}`} onClick={() => GetPageData(number)}>
                          <Link className="page-link" to="#">{number}</Link>
                        </li>
                      ))}
                      <li className={`page-item ${rawResponse.nextPage === null ? "disabled" : ""}`} onClick={GetLastPageData}>
                        <Link className="page-link" to="#">Last</Link>
                      </li>
                    </ul>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default HotelBookings;